/**
 * AccountsScreen
 * Dedicated screen for bank account management and connections
 * Separates account management from transaction viewing (Budget tab)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  TextInput,
  Platform,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { parseISO, differenceInDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import { formatCurrency } from '../utils/formatters';
import { budgetApi } from '../api/endpoints/budget';
import { bankSyncApi, DataProviderConnection, FeatureStatusResponse, SyncLog } from '../api/endpoints/bankSync';
import { subscriptionsApi } from '../api/endpoints/subscriptions';
import PaywallModal from '../components/common/PaywallModal';
import type { FinancialAccount } from '../api/types';
import logger from '../utils/logger';
import { openWhatsAppSupport } from '../utils/whatsapp';
import EditAccountModal from '../components/budget/EditAccountModal';
import ConnectionHealthBanner, { isConnectionUnhealthy, getStaleLabel } from '../components/accounts/ConnectionHealthBanner';

// Brand colors for Coming Soon section
const brandColors = {
  green: '#71B352',
  greenLight: '#E8F5E0',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
};

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  checking: 'Compte Courant',
  savings: 'Epargne',
  investment: 'Investissement',
  credit_card: 'Carte de credit',
  loan: 'Pret',
  other: 'Autre',
};

const CONNECTION_STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  connected: {
    label: 'Connecte',
    color: colors.success,
    icon: 'check-circle',
  },
  syncing: {
    label: 'Synchronisation...',
    color: colors.primary,
    icon: 'sync',
  },
  pending: {
    label: 'En attente',
    color: colors.warning,
    icon: 'clock-outline',
  },
  sca_needed: {
    label: 'Validation requise',
    color: colors.warning,
    icon: 'alert-circle-outline',
  },
  webauth_needed: {
    label: 'Reconnexion requise',
    color: colors.warning,
    icon: 'alert-circle-outline',
  },
  expired: {
    label: 'Session expiree',
    color: colors.error,
    icon: 'clock-alert-outline',
  },
  error: {
    label: 'Erreur',
    color: colors.error,
    icon: 'alert-circle',
  },
  disconnected: {
    label: 'Deconnecte',
    color: colors.textSecondary,
    icon: 'link-off',
  },
};

export default function AccountsScreen() {
  const isFocused = useIsFocused();
  const navigation = useNavigation<any>();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [connections, setConnections] = useState<DataProviderConnection[]>([]);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedBanks, setExpandedBanks] = useState<Set<string>>(new Set());
  const [featureStatus, setFeatureStatus] = useState<FeatureStatusResponse | null>(null);
  const [syncLogs, setSyncLogs] = useState<Record<string, SyncLog[]>>({});
  const [syncLogsVisible, setSyncLogsVisible] = useState<Set<string>>(new Set());
  const [syncLogsLoading, setSyncLogsLoading] = useState<Set<string>>(new Set());
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState<FinancialAccount | null>(null);
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [editingConnectionName, setEditingConnectionName] = useState('');

  useEffect(() => {
    if (isFocused) {
      loadData();
    }
  }, [isFocused]);

  const loadData = async () => {
    try {
      setLoading(true);

      // First check if feature is enabled
      const status = await bankSyncApi.getFeatureStatus();
      setFeatureStatus(status);

      const [subResponse, accts] = await Promise.all([
        subscriptionsApi.getCurrentSubscription(),
        budgetApi.getFinancialAccounts(),
      ]);

      setIsPremium(subResponse.subscription.is_premium);
      setAccounts(accts);

      // Only fetch connections if feature is enabled
      let conns: DataProviderConnection[] = [];
      if (status.enabled) {
        conns = await bankSyncApi.listConnections();
        setConnections(conns);
      } else {
        setConnections([]);
      }

      // Auto-expand banks with issues
      const banksWithIssues = new Set<string>();
      conns.forEach(conn => {
        if (conn.status !== 'connected') {
          banksWithIssues.add(conn.id);
        }
      });
      setExpandedBanks(banksWithIssues);
    } catch (error) {
      logger.error('[AccountsScreen] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, []);

  const handleConnect = () => {
    if (!isPremium) {
      setShowPaywall(true);
      return;
    }
    navigation.navigate('BankConnection');
  };

  const handleImportCSV = () => {
    navigation.navigate('ImportBankStatementsWizard');
  };

  const handleReconnect = async (connection: DataProviderConnection) => {
    if (!isPremium) {
      setShowPaywall(true);
      return;
    }

    try {
      setActionLoading(connection.id);
      const response = await bankSyncApi.getReconnectUrl(connection.id);
      navigation.navigate('BankConnection', {
        mode: 'reconnect',
        connectionId: connection.id,
        redirectUrl: response.redirect_url,
      });
    } catch (error: any) {
      logger.error('[AccountsScreen] Error getting reconnect URL:', error);

      // Token expired - need to initiate a new connection
      if (bankSyncApi.isTokenExpiredError(error)) {
        if (Platform.OS === 'web') {
          if (window.confirm('Votre session bancaire a expiré. Voulez-vous reconnecter votre banque ?')) {
            navigation.navigate('BankConnection' as never);
          }
        } else {
          Alert.alert(
            'Session expirée',
            'Votre session bancaire a expiré. Vous devez reconnecter votre banque.',
            [
              { text: 'Annuler', style: 'cancel' },
              { text: 'Reconnecter', onPress: () => navigation.navigate('BankConnection' as never) },
            ]
          );
        }
      } else {
        if (Platform.OS === 'web') {
          window.alert('Impossible de reconnecter. Veuillez réessayer.');
        } else {
          Alert.alert('Erreur', 'Impossible de reconnecter. Veuillez réessayer.', [
            { text: 'OK', style: 'cancel' },
            { text: 'Contacter le support', onPress: () => openWhatsAppSupport('Erreur reconnexion bancaire') },
          ]);
        }
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleSync = async (connection: DataProviderConnection) => {
    if (!isPremium) {
      setShowPaywall(true);
      return;
    }

    try {
      setActionLoading(connection.id);
      await bankSyncApi.triggerSync(connection.id);
      await loadData();
    } catch (error) {
      logger.error('[AccountsScreen] Error triggering sync:', error);
      if (Platform.OS === 'web') {
        window.alert('Impossible de synchroniser. Veuillez réessayer.');
      } else {
        Alert.alert('Erreur', 'Impossible de synchroniser. Veuillez réessayer.', [
          { text: 'OK', style: 'cancel' },
          { text: 'Contacter le support', onPress: () => openWhatsAppSupport('Erreur synchronisation bancaire') },
        ]);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (connection: DataProviderConnection) => {
    const doDisconnect = async () => {
      try {
        setActionLoading(connection.id);
        await bankSyncApi.disconnect(connection.id);
        await loadData();
      } catch (error) {
        logger.error('[AccountsScreen] Error disconnecting:', error);
        if (Platform.OS === 'web') {
          window.alert('Impossible de supprimer la connexion. Veuillez réessayer.');
        } else {
          Alert.alert('Erreur', 'Impossible de supprimer la connexion. Veuillez réessayer.', [
            { text: 'OK', style: 'cancel' },
            { text: 'Contacter le support', onPress: () => openWhatsAppSupport('Erreur déconnexion bancaire') },
          ]);
        }
      } finally {
        setActionLoading(null);
      }
    };

    const bankName = connection.display_name || connection.institution_name || 'cette banque';
    const message = `Supprimer la connexion "${bankName}" ?\n\nLes transactions deja importees seront conservees.`;

    // Alert.alert is a no-op on web (react-native-web), so use window.confirm
    if (Platform.OS === 'web') {
      if (window.confirm(`Supprimer la connexion "${bankName}" ?\n\nLes transactions deja importees seront conservees.`)) {
        doDisconnect();
      }
    } else {
      Alert.alert(
        'Supprimer la connexion',
        message,
        [
          { text: 'Annuler', style: 'cancel' },
          { text: 'Supprimer', style: 'destructive', onPress: doDisconnect },
        ]
      );
    }
  };

  const handleToggleSyncLogs = async (connectionId: string) => {
    if (syncLogsVisible.has(connectionId)) {
      setSyncLogsVisible(prev => {
        const newSet = new Set(prev);
        newSet.delete(connectionId);
        return newSet;
      });
      return;
    }

    // Lazy-load on first tap
    if (!syncLogs[connectionId]) {
      setSyncLogsLoading(prev => new Set(prev).add(connectionId));
      try {
        const logs = await bankSyncApi.getSyncLogs(connectionId, 10);
        setSyncLogs(prev => ({ ...prev, [connectionId]: logs }));
      } catch (error) {
        logger.error('[AccountsScreen] Error loading sync logs:', error);
      } finally {
        setSyncLogsLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(connectionId);
          return newSet;
        });
      }
    }

    setSyncLogsVisible(prev => new Set(prev).add(connectionId));
  };

  const formatSyncDate = (dateStr: string): string => {
    try {
      return format(parseISO(dateStr), 'd MMM HH:mm', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (ms: number | null): string => {
    if (ms == null) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(0)}s`;
  };

  const handleEditConnectionName = (connection: DataProviderConnection) => {
    const currentName = connection.display_name || connection.institution_name || '';
    setEditingConnectionId(connection.id);
    setEditingConnectionName(currentName);
  };

  const handleSaveConnectionName = async () => {
    if (!editingConnectionId || !editingConnectionName.trim()) return;
    try {
      await bankSyncApi.updateDisplayName(editingConnectionId, editingConnectionName.trim());
      setEditingConnectionId(null);
      setEditingConnectionName('');
      await loadData();
    } catch (error) {
      logger.error('[AccountsScreen] Error updating connection name:', error);
      if (Platform.OS === 'web') {
        window.alert('Impossible de renommer la banque.');
      } else {
        Alert.alert('Erreur', 'Impossible de renommer la banque.');
      }
    }
  };

  const handleCancelEditConnection = () => {
    setEditingConnectionId(null);
    setEditingConnectionName('');
  };

  const toggleBankExpanded = (connectionId: string) => {
    setExpandedBanks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(connectionId)) {
        newSet.delete(connectionId);
      } else {
        newSet.add(connectionId);
      }
      return newSet;
    });
  };

  const formatLastSynced = (dateStr: string | null): string => {
    if (!dateStr) return 'Jamais';
    try {
      const date = parseISO(dateStr);
      const diffDays = differenceInDays(new Date(), date);
      if (diffDays === 0) return "Aujourd'hui";
      if (diffDays === 1) return 'Hier';
      if (diffDays < 7) return `Il y a ${diffDays} jours`;
      if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
      return `Il y a ${Math.floor(diffDays / 30)} mois`;
    } catch {
      return 'Manuel';
    }
  };

  // Group accounts by connection (match by institution_name until connection_id is added)
  const getAccountsForConnection = (connection: DataProviderConnection) => {
    const connName = (connection.institution_name || '').toLowerCase();
    return accounts.filter(a => !a.is_manual && (a.institution_name || '').toLowerCase() === connName);
  };


  // Connections that need attention
  const connectionsNeedingAction = connections.filter(
    c => c.status === 'pending' || c.status === 'error' || c.status === 'sca_needed' || c.status === 'webauth_needed' || c.status === 'expired'
  );

  const connectedConnections = connections.filter(c => c.status === 'connected' || c.status === 'syncing');
  // Calculate totals (ensure numeric values)
  const totalBalance = accounts.reduce((sum, acc) => {
    const balance = typeof acc.balance === 'number' ? acc.balance : parseFloat(acc.balance) || 0;
    return sum + balance;
  }, 0);
  const totalTransactions = accounts.reduce((sum, acc) => {
    const count = typeof acc.transaction_count === 'number' ? acc.transaction_count : parseInt(acc.transaction_count) || 0;
    return sum + count;
  }, 0);

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Chargement des comptes...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Header with totals */}
        <View style={styles.header}>
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>Mes Comptes</Text>
            <Text style={styles.headerSubtitle}>
              {accounts.length} compte{accounts.length > 1 ? 's' : ''} · {totalTransactions} transactions
            </Text>
          </View>
          <View style={styles.headerBalance}>
            <Text style={styles.balanceLabel}>Solde total</Text>
            <Text style={styles.balanceValue}>{formatCurrency(totalBalance)}</Text>
          </View>
        </View>

        {/* Action Required Banner */}
        {connectionsNeedingAction.length > 0 && (
          <View style={styles.actionBanner}>
            <View style={styles.actionBannerIcon}>
              <Icon name="alert-circle" size={24} color={colors.warning} />
            </View>
            <View style={styles.actionBannerContent}>
              <Text style={styles.actionBannerTitle}>
                {connectionsNeedingAction.length} connexion{connectionsNeedingAction.length > 1 ? 's' : ''} necessite{connectionsNeedingAction.length > 1 ? 'nt' : ''} votre attention
              </Text>
              <Text style={styles.actionBannerText}>
                {connectionsNeedingAction.some(c => c.status === 'sca_needed' || c.status === 'webauth_needed')
                  ? 'Validation ou reconnexion requise'
                  : connectionsNeedingAction.some(c => c.status === 'error')
                    ? 'Une erreur de synchronisation est survenue'
                    : 'Connexion en attente de validation'}
              </Text>
            </View>
          </View>
        )}

        {/* Quick Actions */}
        <View style={styles.quickActions}>
          <TouchableOpacity
            style={[
              styles.quickActionPrimary,
              featureStatus?.coming_soon && styles.quickActionComingSoon
            ]}
            onPress={featureStatus?.coming_soon ? undefined : handleConnect}
            disabled={featureStatus?.coming_soon}
          >
            <Icon name="bank-plus" size={20} color="#FFFFFF" />
            <Text style={styles.quickActionPrimaryText}>
              {featureStatus?.coming_soon
                ? 'Bientôt disponible'
                : isPremium
                  ? 'Connecter une banque'
                  : 'Passer Premium'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.quickActionSecondary} onPress={handleImportCSV}>
            <Icon name="file-upload" size={20} color={colors.primary} />
            <Text style={styles.quickActionSecondaryText}>Import CSV</Text>
          </TouchableOpacity>
        </View>

        {/* Coming Soon Banner */}
        {featureStatus?.coming_soon && (
          <View style={styles.comingSoonBanner}>
            <View style={styles.comingSoonHeader}>
              <View style={styles.comingSoonBadge}>
                <Icon name="rocket-launch" size={16} color={brandColors.orange} />
                <Text style={styles.comingSoonBadgeText}>Bientôt disponible</Text>
              </View>
              {featureStatus.estimated_launch && (
                <Text style={styles.comingSoonDate}>{featureStatus.estimated_launch}</Text>
              )}
            </View>

            <Text style={styles.comingSoonTitle}>{featureStatus.feature_name}</Text>
            <Text style={styles.comingSoonDescription}>{featureStatus.description}</Text>

            <View style={styles.comingSoonBenefits}>
              {featureStatus.benefits.map((benefit, index) => (
                <View key={index} style={styles.comingSoonBenefitRow}>
                  <Icon name="check-circle" size={16} color={brandColors.green} />
                  <Text style={styles.comingSoonBenefitText}>{benefit}</Text>
                </View>
              ))}
            </View>

            <View style={styles.comingSoonCta}>
              <Text style={styles.comingSoonCtaText}>
                En attendant, importez vos relevés bancaires au format CSV
              </Text>
              <TouchableOpacity style={styles.comingSoonCtaButton} onPress={handleImportCSV}>
                <Icon name="file-upload" size={18} color={brandColors.green} />
                <Text style={styles.comingSoonCtaButtonText}>Importer un CSV</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Connected Banks Section */}
        {connections.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Banques connectees</Text>

            {connections.map(connection => {
              const unhealthy = isConnectionUnhealthy({
                id: connection.id,
                status: connection.status,
                last_synced_at: connection.last_synced_at,
              });
              const staleInfo = getStaleLabel({
                id: connection.id,
                status: connection.status,
                last_synced_at: connection.last_synced_at,
              });

              // Override status label for stale connected connections
              const status = staleInfo
                ? { label: staleInfo.label, color: staleInfo.color, icon: staleInfo.color === '#EF4444' ? 'alert-circle' : 'alert-outline' }
                : (CONNECTION_STATUS_CONFIG[connection.status] || CONNECTION_STATUS_CONFIG.error);

              const isExpanded = expandedBanks.has(connection.id);
              const needsReconnect = connection.status === 'sca_needed' || connection.status === 'webauth_needed' || connection.status === 'expired';
              const hasError = connection.status === 'error';
              const isConnected = connection.status === 'connected';
              const isPending = connection.status === 'pending';
              const isSyncing = connection.status === 'syncing';

              // Get display info
              const bankDisplayName = connection.display_name || connection.institution_name || connection.provider?.display_name || 'Banque';
              const accountInfo = connection.masked_account_number;

              // Get contextual help message
              const getHelpMessage = () => {
                if (isPending) {
                  return 'Finalisez la connexion dans la fenetre de votre banque. Si la fenetre s\'est fermee, cliquez sur "Reconnecter".';
                }
                if (connection.status === 'expired') {
                  return 'Votre session bancaire a expire. Cliquez sur "Reconnecter" pour relancer la connexion.';
                }
                if (needsReconnect) {
                  return 'Votre banque demande une nouvelle authentification. Cliquez sur "Reconnecter" pour valider.';
                }
                if (hasError) {
                  return 'La connexion a echoue. Essayez de reconnecter ou supprimez cette connexion.';
                }
                return null;
              };

              const helpMessage = getHelpMessage();

              return (
                <React.Fragment key={connection.id}>
                  {/* Connection Health Banner */}
                  {unhealthy && (
                    <ConnectionHealthBanner
                      connection={{
                        id: connection.id,
                        status: connection.status,
                        last_synced_at: connection.last_synced_at,
                        bank_name: bankDisplayName,
                      }}
                      onReconnect={() => handleReconnect(connection)}
                    />
                  )}
                <View style={[
                  styles.bankCard,
                  (needsReconnect || hasError || isPending) && styles.bankCardWarning,
                ]}>
                  {/* Bank Header */}
                  <TouchableOpacity
                    style={styles.bankCardHeader}
                    onPress={() => toggleBankExpanded(connection.id)}
                  >
                    <View style={styles.bankCardLeft}>
                      <View style={[styles.bankLogo, { backgroundColor: status.color + '20' }]}>
                        <Icon name="bank" size={20} color={status.color} />
                      </View>
                      <View style={styles.bankInfo}>
                        {editingConnectionId === connection.id ? (
                          <View style={styles.bankNameEditRow}>
                            <TextInput
                              style={styles.bankNameInput}
                              value={editingConnectionName}
                              onChangeText={setEditingConnectionName}
                              autoFocus
                              selectTextOnFocus
                              onSubmitEditing={handleSaveConnectionName}
                            />
                            <TouchableOpacity
                              onPress={handleSaveConnectionName}
                              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            >
                              <Icon name="check" size={18} color={colors.success} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={handleCancelEditConnection}
                              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            >
                              <Icon name="close" size={18} color={colors.textSecondary} />
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <View style={styles.bankNameRow}>
                            <Text style={styles.bankName}>{bankDisplayName}</Text>
                            <TouchableOpacity
                              onPress={() => handleEditConnectionName(connection)}
                              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              style={styles.bankNameEditIcon}
                            >
                              <Icon name="pencil-outline" size={14} color={colors.primary} />
                            </TouchableOpacity>
                          </View>
                        )}
                        {accountInfo && (
                          <Text style={styles.accountNumber}>{accountInfo}</Text>
                        )}
                        <View style={styles.statusRow}>
                          <View style={[styles.statusBadge, { backgroundColor: status.color + '20' }]}>
                            <Icon name={status.icon} size={12} color={status.color} />
                            <Text style={[styles.statusText, { color: status.color }]}>
                              {status.label}
                            </Text>
                          </View>
                          {connection.last_synced_at && !isPending && (
                            <Text style={styles.lastSync}>
                              · {formatLastSynced(connection.last_synced_at)}
                            </Text>
                          )}
                        </View>
                      </View>
                    </View>
                    <Icon
                      name={isExpanded ? 'chevron-up' : 'chevron-down'}
                      size={20}
                      color={colors.textSecondary}
                    />
                  </TouchableOpacity>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <View style={styles.bankCardExpanded}>
                      {/* Help message for pending/error states */}
                      {helpMessage && (
                        <View style={[styles.helpMessage, hasError && styles.helpMessageError]}>
                          <Icon name="information" size={16} color={hasError ? colors.error : colors.warning} />
                          <Text style={[styles.helpMessageText, hasError && styles.helpMessageTextError]}>
                            {helpMessage}
                          </Text>
                        </View>
                      )}

                      {/* Error details if any */}
                      {connection.last_sync_error_message && !helpMessage && (
                        <View style={styles.errorMessage}>
                          <Icon name="information" size={16} color={colors.error} />
                          <Text style={styles.errorMessageText}>
                            {connection.last_sync_error_message}
                          </Text>
                        </View>
                      )}

                      {/* Accounts under this bank */}
                      {getAccountsForConnection(connection).map(account => (
                        <View key={account.id} style={styles.bankAccountRow}>
                          <View style={styles.bankAccountInfo}>
                            <Text style={styles.bankAccountName} numberOfLines={1}>{account.name}</Text>
                            <Text style={styles.bankAccountMeta}>
                              {ACCOUNT_TYPE_LABELS[account.account_type] || account.account_type}
                              {account.iban_masked && ` · ${account.iban_masked}`}
                              {' · '}{formatCurrency(account.balance)}
                            </Text>
                          </View>
                          <TouchableOpacity
                            style={styles.bankAccountEditButton}
                            onPress={() => {
                              setEditingAccount(account);
                              setShowEditAccount(true);
                            }}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                          >
                            <Icon name="pencil-outline" size={16} color={colors.primary} />
                          </TouchableOpacity>
                        </View>
                      ))}

                      {/* Sync History */}
                      {syncLogsVisible.has(connection.id) && syncLogs[connection.id] && (
                        <View style={styles.syncHistorySection}>
                          <Text style={styles.syncHistoryTitle}>Historique des syncs</Text>
                          {syncLogs[connection.id].length === 0 ? (
                            <Text style={styles.syncHistoryEmpty}>Aucune synchronisation</Text>
                          ) : (
                            syncLogs[connection.id].map(log => {
                              const isCompleted = log.status === 'completed';
                              const isFailed = log.status === 'failed';
                              const totalTx = log.transactions_created + log.transactions_updated;
                              return (
                                <View key={log.id} style={styles.syncLogRow}>
                                  <Icon
                                    name={isCompleted ? 'check-circle' : isFailed ? 'close-circle' : 'clock-outline'}
                                    size={14}
                                    color={isCompleted ? colors.success : isFailed ? colors.error : colors.textSecondary}
                                  />
                                  <Text style={[
                                    styles.syncLogText,
                                    isFailed && styles.syncLogTextError,
                                  ]}>
                                    {formatSyncDate(log.started_at)}
                                    {isCompleted && ` · ${log.accounts_synced} compte${log.accounts_synced > 1 ? 's' : ''} · ${totalTx} transaction${totalTx > 1 ? 's' : ''}`}
                                    {isCompleted && log.duration_ms != null && ` · ${formatDuration(log.duration_ms)}`}
                                    {isFailed && log.error_message && ` · Erreur: ${log.error_message}`}
                                  </Text>
                                </View>
                              );
                            })
                          )}
                        </View>
                      )}
                      {syncLogsLoading.has(connection.id) && (
                        <ActivityIndicator size="small" color={colors.primary} style={{ marginBottom: spacing.sm }} />
                      )}

                      {/* Action buttons */}
                      <View style={styles.bankActions}>
                        {/* Reconnect button for SCA/webauth/error/pending */}
                        {(needsReconnect || hasError || isPending) && (
                          <TouchableOpacity
                            style={styles.reconnectButton}
                            onPress={() => handleReconnect(connection)}
                            disabled={actionLoading === connection.id}
                          >
                            {actionLoading === connection.id ? (
                              <ActivityIndicator size="small" color={colors.warning} />
                            ) : (
                              <>
                                <Icon name="refresh" size={16} color={colors.warning} />
                                <Text style={styles.reconnectButtonText}>Reconnecter</Text>
                              </>
                            )}
                          </TouchableOpacity>
                        )}

                        {/* Sync button for connected */}
                        {isConnected && (
                          <TouchableOpacity
                            style={styles.syncButton}
                            onPress={() => handleSync(connection)}
                            disabled={actionLoading === connection.id}
                          >
                            {actionLoading === connection.id ? (
                              <ActivityIndicator size="small" color={colors.primary} />
                            ) : (
                              <>
                                <Icon name="sync" size={16} color={colors.primary} />
                                <Text style={styles.syncButtonText}>Synchroniser</Text>
                              </>
                            )}
                          </TouchableOpacity>
                        )}

                        {/* History button */}
                        <TouchableOpacity
                          style={styles.historyButton}
                          onPress={() => handleToggleSyncLogs(connection.id)}
                          disabled={syncLogsLoading.has(connection.id)}
                        >
                          <Icon name="history" size={16} color={colors.textSecondary} />
                          <Text style={styles.historyButtonText}>Historique</Text>
                        </TouchableOpacity>

                        {/* Delete button - always available */}
                        <TouchableOpacity
                          style={styles.disconnectButton}
                          onPress={() => handleDisconnect(connection)}
                          disabled={actionLoading === connection.id}
                        >
                          <Icon name="delete-outline" size={16} color={colors.error} />
                          <Text style={styles.disconnectButtonText}>Supprimer</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                </View>
                </React.Fragment>
              );
            })}
          </View>
        )}

        {/* All Accounts Section */}
        {accounts.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Tous les comptes</Text>
              <TouchableOpacity style={styles.sectionAction} onPress={handleImportCSV}>
                <Icon name="plus" size={16} color={colors.primary} />
                <Text style={styles.sectionActionText}>Ajouter</Text>
              </TouchableOpacity>
            </View>

            {accounts.map(account => (
              <View key={account.id} style={styles.accountCard}>
                <View style={styles.accountCardLeft}>
                  <View style={styles.accountIcon}>
                    <Icon
                      name={account.is_manual ? 'wallet' : 'bank'}
                      size={18}
                      color={colors.primary}
                    />
                  </View>
                  <View style={styles.accountInfo}>
                    <Text style={styles.accountName}>{account.name}</Text>
                    <Text style={styles.accountType}>
                      {ACCOUNT_TYPE_LABELS[account.account_type] || account.account_type}
                      {account.institution_name && ` · ${account.institution_name}`}
                    </Text>
                    {account.iban_masked && (
                      <Text style={styles.accountIban}>{account.iban_masked}</Text>
                    )}
                  </View>
                </View>
                <View style={styles.accountCardRight}>
                  <Text style={styles.accountBalance}>{formatCurrency(account.balance)}</Text>
                  <Text style={styles.accountTransactions}>
                    {account.transaction_count} tx
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.accountEditButton}
                  onPress={() => {
                    setEditingAccount(account);
                    setShowEditAccount(true);
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Icon name="pencil-outline" size={16} color={colors.primary} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}

        {/* Empty State */}
        {connections.length === 0 && accounts.length === 0 && (
          <View style={styles.emptyState}>
            <View style={styles.emptyIcon}>
              <Icon name="bank-plus" size={48} color={colors.primary} />
            </View>
            <Text style={styles.emptyTitle}>Aucun compte</Text>
            <Text style={styles.emptyText}>
              Connectez votre banque pour synchroniser automatiquement vos transactions, ou importez un releve CSV.
            </Text>
            <View style={styles.emptyFeatures}>
              <View style={styles.emptyFeature}>
                <Icon name="check-circle" size={16} color={colors.success} />
                <Text style={styles.emptyFeatureText}>Import automatique</Text>
              </View>
              <View style={styles.emptyFeature}>
                <Icon name="check-circle" size={16} color={colors.success} />
                <Text style={styles.emptyFeatureText}>Categorisation IA</Text>
              </View>
              <View style={styles.emptyFeature}>
                <Icon name="check-circle" size={16} color={colors.success} />
                <Text style={styles.emptyFeatureText}>Alertes intelligentes</Text>
              </View>
            </View>
          </View>
        )}

        {/* Premium info for non-premium users */}
        {!isPremium && (
          <View style={styles.premiumPromo}>
            <Icon name="crown" size={24} color={colors.primary} />
            <View style={styles.premiumPromoContent}>
              <Text style={styles.premiumPromoTitle}>Synchronisation bancaire</Text>
              <Text style={styles.premiumPromoText}>
                Connectez vos banques automatiquement avec l'abonnement Premium.
              </Text>
            </View>
            <TouchableOpacity
              style={styles.premiumPromoButton}
              onPress={() => setShowPaywall(true)}
            >
              <Text style={styles.premiumPromoButtonText}>Decouvrir</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.bottomPadding} />
      </ScrollView>

      <EditAccountModal
        visible={showEditAccount}
        onClose={() => { setShowEditAccount(false); setEditingAccount(null); }}
        account={editingAccount}
        onSaved={loadData}
      />

      <PaywallModal
        visible={showPaywall}
        onClose={() => setShowPaywall(false)}
        trigger="feature"
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  headerBalance: {
    alignItems: 'flex-end',
  },
  balanceLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  balanceValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },

  // Action Banner
  actionBanner: {
    flexDirection: 'row',
    backgroundColor: colors.warning + '15',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.warning + '30',
  },
  actionBannerIcon: {
    marginRight: spacing.md,
  },
  actionBannerContent: {
    flex: 1,
  },
  actionBannerTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: 2,
  },
  actionBannerText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },

  // Quick Actions
  quickActions: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  quickActionPrimary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 12,
    gap: spacing.sm,
  },
  quickActionPrimaryText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
  quickActionSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '10',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
    gap: spacing.xs,
  },
  quickActionSecondaryText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.primary,
  },

  // Section
  section: {
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  sectionAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sectionActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },

  // Bank Card
  bankCard: {
    backgroundColor: colors.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
    overflow: 'hidden',
  },
  bankCardWarning: {
    borderColor: colors.warning + '50',
    backgroundColor: colors.warning + '05',
  },
  bankCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  bankCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  bankLogo: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  bankInfo: {
    flex: 1,
  },
  bankNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  bankName: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  bankNameEditIcon: {
    marginLeft: spacing.xs,
  },
  bankNameEditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: 2,
  },
  bankNameInput: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.primary,
    paddingVertical: 2,
    paddingHorizontal: spacing.xs,
  },
  accountNumber: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
    gap: 4,
  },
  statusText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium as '500',
  },
  lastSync: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
  },
  bankCardExpanded: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    padding: spacing.md,
    backgroundColor: colors.backgroundSecondary,
  },
  bankAccountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.xs,
  },
  bankAccountInfo: {
    flex: 1,
  },
  bankAccountName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium as '500',
    color: colors.text,
  },
  bankAccountMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  bankAccountEditButton: {
    padding: spacing.xs,
    marginLeft: spacing.sm,
  },
  errorMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.error + '10',
    padding: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  errorMessageText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.error,
    lineHeight: 16,
  },
  helpMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.warning + '10',
    padding: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  helpMessageError: {
    backgroundColor: colors.error + '10',
  },
  helpMessageText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    lineHeight: 16,
  },
  helpMessageTextError: {
    color: colors.error,
  },
  bankActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    gap: 4,
  },
  syncButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  reconnectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.warning + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    gap: 4,
  },
  reconnectButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontWeight: typography.fontWeight.medium as '500',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.error + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    gap: 4,
  },
  retryButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontWeight: typography.fontWeight.medium as '500',
  },
  historyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.textSecondary + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    gap: 4,
  },
  historyButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  syncHistorySection: {
    marginBottom: spacing.sm,
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: spacing.sm,
  },
  syncHistoryTitle: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  syncHistoryEmpty: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  syncLogRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    paddingVertical: 3,
  },
  syncLogText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    lineHeight: 16,
  },
  syncLogTextError: {
    color: colors.error,
  },
  disconnectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.error + '10',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.error + '30',
    gap: 4,
    marginLeft: 'auto',
  },
  disconnectButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontWeight: typography.fontWeight.medium as '500',
  },

  // Account Card
  accountCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.background,
    borderRadius: 10,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.sm,
  },
  accountCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  accountIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  accountInfo: {
    flex: 1,
  },
  accountName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
  },
  accountType: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  accountCardRight: {
    alignItems: 'flex-end',
  },
  accountBalance: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
  },
  accountTransactions: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
  accountIban: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 1,
  },
  accountEditButton: {
    marginLeft: spacing.sm,
    padding: spacing.xs,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    padding: spacing.xl,
    marginHorizontal: spacing.lg,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: spacing.lg,
  },
  emptyFeatures: {
    alignSelf: 'stretch',
  },
  emptyFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  emptyFeatureText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },

  // Premium Promo
  premiumPromo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '08',
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.primary + '20',
    gap: spacing.md,
  },
  premiumPromoContent: {
    flex: 1,
  },
  premiumPromoTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.text,
    marginBottom: 2,
  },
  premiumPromoText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    lineHeight: 16,
  },
  premiumPromoButton: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
  },
  premiumPromoButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },

  bottomPadding: {
    height: spacing.xxl,
  },

  // Coming Soon styles
  quickActionComingSoon: {
    backgroundColor: brandColors.orange,
    opacity: 0.9,
  },
  comingSoonBanner: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.lg,
    backgroundColor: brandColors.orangeLight,
    borderRadius: 16,
    padding: spacing.lg,
    borderWidth: 2,
    borderColor: brandColors.orange + '30',
  },
  comingSoonHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  comingSoonBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: brandColors.orange + '20',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    gap: 6,
  },
  comingSoonBadgeText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: brandColors.orange,
  },
  comingSoonDate: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium as '500',
    color: brandColors.orange,
  },
  comingSoonTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold as '700',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  comingSoonDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 20,
    marginBottom: spacing.md,
  },
  comingSoonBenefits: {
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  comingSoonBenefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  comingSoonBenefitText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  comingSoonCta: {
    backgroundColor: brandColors.greenLight,
    padding: spacing.md,
    borderRadius: 12,
    alignItems: 'center',
  },
  comingSoonCtaText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  comingSoonCtaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: brandColors.green,
  },
  comingSoonCtaButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: brandColors.green,
  },
});
